// Phase 2 — BRC AAR Work Details validation rules.
// Mirror of the backend regex/length rules in _validate_aar_row_v2(). The
// backend is still the security boundary; the frontend uses these to surface
// per-cell errors and block Save without a round-trip.

const RX_ALNUM = /^[A-Za-z0-9]+$/;
const RX_DIGITS = /^\d+$/;
// QTY (2026-06-03): integer 1-3 digits with optional one-decimal tenths place.
const RX_QTY = /^\d{1,3}(\.\d)?$/;
const RX_JC = /^\d{4}(\.[A-Za-z0-9]{1,10})?$/;
const RX_HRS = /^\d{1,3}(\.\d{1,3})?$/;

const NO_SPACES = (v) => !(typeof v === 'string' && v.includes(' '));

/**
 * "Today" anchored to US Eastern, formatted as YYYY-MM-DD. Used to gate
 * future-dated AAR line items so the rule stays predictable regardless of the
 * client's local timezone or the server's UTC clock.
 */
function todayEasternStr() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()); // en-CA yields "YYYY-MM-DD"
}

export { todayEasternStr };

/** Return { field: 'message' } for a single row, omitting healthy fields. */
export function validateAARRow(row) {
  if (!row || row.row_status === 'VOIDED') return {};
  const errs = {};
  const v = (k) => (row?.[k] || '').toString().trim();

  // Description — required, ≤120, spaces allowed
  const desc = v('description');
  if (!desc) errs.description = 'Description is required';
  else if (desc.length > 120) errs.description = 'Max 120 characters';

  // DATE — required (Phase 2 polish 2026-06-01). No future dates (2026-06-02):
  // line-item dates represent completed work, so they must be ≤ today in US Eastern.
  const workDate = v('work_date');
  if (!workDate) {
    errs.work_date = 'Date is required';
  } else if (workDate > todayEasternStr()) {
    // YYYY-MM-DD strings sort lexicographically the same as chronologically.
    errs.work_date = 'Date cannot be in the future';
  }

  // WDB — required. Backed by wdb_user_id (modal selection). Phase 2 polish.
  if (!row.wdb_user_id) errs.wdb = 'Work Done By is required';

  // QVB — required. Backed by qvb_user_id (modal selection). Phase 2 polish.
  if (!row.qvb_user_id) errs.qvb = 'Quality Verified By is required';

  // LOC — required, alnum, no spaces, ≤4
  const loc = v('loc');
  if (!loc) errs.loc = 'Location is required';
  else if (!NO_SPACES(loc)) errs.loc = 'No spaces allowed';
  else if (loc.length > 4) errs.loc = 'Max 4 characters';
  else if (!RX_ALNUM.test(loc)) errs.loc = 'Letters and numbers only';

  // QTY — required. 1-3 integer digits with one optional decimal (e.g.,
  // "10.5"). The regex bounds size; no separate length check is needed.
  const qty = v('qty');
  if (!qty) errs.qty = 'Quantity is required';
  else if (!NO_SPACES(qty)) errs.qty = 'No spaces allowed';
  else if (!RX_QTY.test(qty)) errs.qty = 'Numeric, up to 3 digits with one optional decimal (e.g., 10.5)';

  // CC — required, alnum, no spaces, ≤2
  const cc = v('cc');
  if (!cc) errs.cc = 'CC is required';
  else if (!NO_SPACES(cc)) errs.cc = 'No spaces allowed';
  else if (cc.length > 2) errs.cc = 'Max 2 characters';
  else if (!RX_ALNUM.test(cc)) errs.cc = 'Letters and numbers only';

  // QFA — optional, alnum, no spaces, ≤2
  const qfa = v('qfa');
  if (qfa) {
    if (!NO_SPACES(qfa)) errs.qfa = 'No spaces allowed';
    else if (qfa.length > 2) errs.qfa = 'Max 2 characters';
    else if (!RX_ALNUM.test(qfa)) errs.qfa = 'Letters and numbers only';
  }

  // JC APP — required, 4 digits + optional .alnum extension up to 10
  const jcApp = v('jc_app');
  if (!jcApp) errs.jc_app = 'JC APP is required';
  else if (!NO_SPACES(jcApp)) errs.jc_app = 'No spaces allowed';
  else if (!RX_JC.test(jcApp)) errs.jc_app = '4 digits, optional .<ext>';

  // WM — required, alnum, ≤2
  const wm = v('wm');
  if (!wm) errs.wm = 'WM is required';
  else if (!NO_SPACES(wm)) errs.wm = 'No spaces allowed';
  else if (wm.length > 2) errs.wm = 'Max 2 characters';
  else if (!RX_ALNUM.test(wm)) errs.wm = 'Letters and numbers only';

  // JC REM — optional, same format as JC APP
  const jcRem = v('jc_rem');
  if (jcRem) {
    if (!NO_SPACES(jcRem)) errs.jc_rem = 'No spaces allowed';
    else if (!RX_JC.test(jcRem)) errs.jc_rem = '4 digits, optional .<ext>';
  }

  // RC — optional, alnum, ≤2
  const rc = v('rc');
  if (rc) {
    if (!NO_SPACES(rc)) errs.rc = 'No spaces allowed';
    else if (rc.length > 2) errs.rc = 'Max 2 characters';
    else if (!RX_ALNUM.test(rc)) errs.rc = 'Letters and numbers only';
  }

  // HRS — required, ≤3 digits before and after decimal
  const hrs = v('hrs');
  if (!hrs) errs.hrs = 'Hours required';
  else if (!NO_SPACES(hrs)) errs.hrs = 'No spaces allowed';
  else if (!RX_HRS.test(hrs)) errs.hrs = 'Numeric (e.g. 0.040)';

  return errs;
}

/** Return per-row error maps. Returns [] when all rows are clean. */
export function validateAARRows(rows) {
  return (rows || []).map((r) => validateAARRow(r));
}

export function rowsHaveErrors(perRowErrors) {
  return (perRowErrors || []).some((e) => e && Object.keys(e).length > 0);
}
